/**
 * ArenaTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import { Pill } from "@/components/palgame/Pill";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function ArenaTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState("bronze");
  const [pals, setPals] = useState([]);
  const [teams, setTeams] = useState([]);
  const [chosen, setChosen] = useState([]);  // up to 3 pal names
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [savingTeam, setSavingTeam] = useState(false);
  const [teamName, setTeamName] = useState("");

  useEffect(() => {
    api.get("/palgame/arena/tiers").then(({ data }) => setTiers(data.tiers || []));
    api.get("/palgame/box").then(({ data }) => setPals(data.pals || []));
    api.get("/palgame/arena/teams").then(({ data }) => setTeams(data.teams || []));
  }, []);

  const tierCfg = useMemo(() => tiers.find((t) => t.id === tier), [tiers, tier]);

  const togglePick = (name) => {
    setChosen((cur) => {
      if (cur.includes(name)) return cur.filter((n) => n !== name);
      if (cur.length >= 3) return cur;
      return [...cur, name];
    });
  };

  const loadTeam = (t) => {
    setChosen([t.pal1, t.pal2, t.pal3]);
    toast.success(`Loaded team: ${t.team_name}`);
  };

  const saveTeam = async () => {
    if (chosen.length !== 3) { toast.error("Pick exactly 3 pals first"); return; }
    if (!teamName.trim()) { toast.error("Team name required"); return; }
    setSavingTeam(true);
    try {
      await api.post("/palgame/arena/team", {
        team_name: teamName.trim(),
        pal1: chosen[0], pal2: chosen[1], pal3: chosen[2],
      });
      const { data } = await api.get("/palgame/arena/teams");
      setTeams(data.teams || []);
      setTeamName("");
      toast.success("Team saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingTeam(false);
    }
  };

  const deleteTeam = async (name) => {
    if (!window.confirm(`Delete team "${name}"?`)) return;
    try {
      await api.delete(`/palgame/arena/team/${encodeURIComponent(name)}`);
      const { data } = await api.get("/palgame/arena/teams");
      setTeams(data.teams || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  const fight = async () => {
    if (chosen.length !== 3) { toast.error("Pick exactly 3 pals first"); return; }
    if (!tierCfg) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/palgame/arena/battle", {
        tier, pal1: chosen[0], pal2: chosen[1], pal3: chosen[2],
      });
      setResult(data);
      if (data.won) {
        toast.success(`+${data.reward_skulls} skulls!`);
        // Tier-name baked into the label so a master-arena win reads
        // differently from a beginner one.
        firePalgamePayout(
          data.reward_skulls,
          `${(tierCfg?.label || "").toUpperCase()} ARENA WIN`.trim() || "ARENA WIN",
        );
      } else {
        toast.error(`Defeat — entry fee lost`);
        firePalgameDefeat(
          tierCfg?.entry || 0,
          `${(tierCfg?.label || "").toUpperCase()} ARENA DEFEAT`.trim() || "ARENA DEFEAT",
        );
      }
      refreshProfile();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="arena-tab">
      {/* Tier selector */}
      <div className="glass-card p-5">
        <h3 className="label mb-3">Choose Arena Tier</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {tiers.map((t) => (
            <button
              key={t.id}
              onClick={() => setTier(t.id)}
              data-testid={`arena-tier-${t.id}`}
              className={`rounded-md p-3 text-left transition-colors border ${
                tier === t.id ? "border-[var(--teal)] bg-[var(--teal-soft)]" : "border-white/5 hover:border-[var(--teal-soft)]"
              }`}
            >
              <div className="font-semibold text-sm">{t.label}</div>
              <div className="text-[10px] text-[var(--muted)] uppercase mt-1">Entry · {t.entry}</div>
              <div className="text-[10px] text-[var(--muted)] uppercase">Win · {Math.round(t.win_rate * 100)}%</div>
              <div className="text-[10px] glow-teal mt-1">{t.skulls_min.toLocaleString()}–{t.skulls_max.toLocaleString()} skulls</div>
            </button>
          ))}
        </div>
      </div>

      {/* Saved teams */}
      {teams.length > 0 && (
        <div className="glass-card p-5">
          <h3 className="label mb-3">Saved Teams</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {teams.map((t) => (
              <div key={t.team_name} className="flex items-center justify-between rounded bg-black/30 px-3 py-2 border border-white/5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.team_name}</div>
                  <div className="text-[10px] text-[var(--muted)] truncate">{t.pal1} · {t.pal2} · {t.pal3}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => loadTeam(t)} className="btn btn-sm" data-testid={`arena-team-load-${t.team_name}`}>Load</button>
                  <button onClick={() => deleteTeam(t.team_name)} className="btn btn-sm" data-testid={`arena-team-delete-${t.team_name}`}>×</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pal picker */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="label">Pick 3 Pals · Selected: {chosen.length}/3</h3>
          {chosen.length === 3 && (
            <div className="flex gap-2">
              <input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Team name"
                className="input text-sm w-32"
                data-testid="arena-team-name-input"
              />
              <button onClick={saveTeam} disabled={savingTeam} className="btn btn-sm" data-testid="arena-save-team-btn">
                {savingTeam ? "Saving…" : "Save Team"}
              </button>
            </div>
          )}
        </div>

        {pals.length < 3 ? (
          <p className="text-sm text-[var(--muted)] py-6 text-center">
            You need at least 3 pals to enter the arena. Catch more in the Catch tab!
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[420px] overflow-y-auto">
            {pals.map((p) => {
              const picked = chosen.includes(p.pal_name);
              return (
                <button
                  key={p.pal_name}
                  onClick={() => togglePick(p.pal_name)}
                  disabled={!picked && chosen.length >= 3}
                  data-testid={`arena-pal-pick-${p.pal_name}`}
                  className={`flex flex-col items-center rounded-md p-2 border transition-colors ${
                    picked ? "border-[var(--teal)] bg-[var(--teal-soft)]" : "border-white/5 hover:border-[var(--teal-soft)]"
                  } ${!picked && chosen.length >= 3 ? "opacity-30 cursor-not-allowed" : ""}`}
                >
                  <PalPortrait name={p.pal_name} size="w-16 h-16" />
                  <div className="text-[10px] mt-1 truncate w-full text-center">{p.pal_name}</div>
                  <div className="text-[9px] text-[var(--muted)]">Lv {p.level}</div>
                </button>
              );
            })}
          </div>
        )}

        <button
          onClick={fight}
          disabled={busy || chosen.length !== 3 || pals.length < 3}
          className="btn w-full mt-4"
          data-testid="arena-fight-btn"
        >
          {busy ? "Battling…" : tierCfg ? `Enter ${tierCfg.label} Arena (${tierCfg.entry} skulls)` : "Loading…"}
        </button>
      </div>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className={`glass-card p-6 ${result.won ? "border-[var(--teal)]" : "border-[var(--warning)]"}`}
            data-testid="arena-result"
          >
            <h3 className={`text-2xl mb-2 ${result.won ? "glow-teal" : "text-[var(--warning)]"}`}>
              {result.won ? "Victory!" : "Defeat"}
            </h3>
            <p className="text-sm text-[var(--muted)] mb-4">{result.message}</p>

            {result.won && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <Pill label="Skulls" value={`+${result.reward_skulls.toLocaleString()}`} highlight />
                  {Object.entries(result.xp_per_pal).map(([pal, xp]) => (
                    <Pill key={pal} label={pal} value={`+${xp} XP`} />
                  ))}
                </div>
                {Object.keys(result.level_ups).length > 0 && (
                  <div className="text-sm">
                    <div className="label mb-1">Level Ups</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(result.level_ups).map(([pal, lv]) => (
                        <span key={pal} className="text-xs px-2 py-1 rounded bg-[var(--teal-soft)] text-[var(--teal)] border border-[var(--teal)]">
                          {pal} → Lv {lv}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}



// ============================================================================
// EXPEDITION TAB — send 2 pals on a 2h expedition for skulls + XP
// ============================================================================

